import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getToken, setToken, clearToken } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setLoading(false);
      return data;
    } catch (e) {
      // Only drop the session on an actual auth failure (401). A transient
      // network error should NOT sign the user out.
      if (e?.response?.status === 401) {
        clearToken();
        setUser(null);
      }
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password });
    // If 2FA is required, return the response without setting user yet
    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.token, remember };
    }
    setToken(data.token, remember);
    setUser(data.user);
    return { requires_2fa: false, user: data.user };
  };

  const loginWith2fa = async (temp_token, code, remember = true) => {
    const { data } = await api.post("/auth/2fa/login", { temp_token, code });
    setToken(data.token, remember);
    setUser(data.user);
    return data.user;
  };

  const signup = async (email, password, full_name, referral_code, consent) => {
    const { data } = await api.post("/auth/signup", {
      email, password, full_name,
      referral_code: referral_code || null,
      accept_terms: consent?.accept_terms ?? true,
      age_confirmed: consent?.age_confirmed ?? true,
    });
    setToken(data.token, true);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, loginWith2fa, signup, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
